import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CountryCode } from 'libphonenumber-js/max';
import { Input } from '@/components/ui/input';
import { DEFAULT_PHONE_COUNTRY, isValidPhoneNumber } from '@/utils/phoneNumbers';
import { CountrySelect } from './CountrySelect';
import { resolveCountryDisplayName } from './countryOptions';
import { derivePhoneInputState } from './phoneInputState';

interface PhoneInputProps {
  value: string;
  onChange: (nextValue: string) => void;
  inputId?: string;
  ariaLabel?: string;
  placeholder?: string;
  hasError?: boolean;
  isDisabled?: boolean;
  isValidationVisible?: boolean;
  className?: string;
}

export function PhoneInput({
  value,
  onChange,
  inputId,
  ariaLabel,
  placeholder,
  hasError = false,
  isDisabled = false,
  isValidationVisible = false,
  className,
}: PhoneInputProps) {
  const { t, i18n } = useTranslation();
  const [hasLeftField, setHasLeftField] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    function resolveInitialCountry() {
      return derivePhoneInputState(value, DEFAULT_PHONE_COUNTRY).country;
    }
  );

  const derivedState = useMemo(
    function computeDerivedState() {
      return derivePhoneInputState(value, selectedCountry);
    },
    [value, selectedCountry]
  );

  useEffect(
    function syncSelectedCountryFromValue() {
      if (derivedState.country !== selectedCountry) {
        setSelectedCountry(derivedState.country);
      }
    },
    [derivedState.country, selectedCountry]
  );

  const isNumberInvalid = value.trim().length > 0 && isValidPhoneNumber(value) === false;
  const isInvalidNumberShown = isNumberInvalid && (hasLeftField || isValidationVisible);

  function handleCountryChange(nextCountry: CountryCode) {
    const nextState = derivePhoneInputState(derivedState.nationalDisplayText, nextCountry);
    setSelectedCountry(nextCountry);
    onChange(nextState.emittedValue);
  }

  function handleNationalTextChange(rawText: string) {
    const nextState = derivePhoneInputState(rawText, selectedCountry);
    setSelectedCountry(nextState.country);
    onChange(nextState.emittedValue);
  }

  return (
    <div className={className}>
      <div className="flex gap-2">
        <div className="w-[132px] shrink-0">
          <CountrySelect
            country={selectedCountry}
            onChange={handleCountryChange}
            inputId={(inputId === undefined ? 'phone-input' : inputId) + '-country'}
            isDisabled={isDisabled}
            hasError={hasError || isInvalidNumberShown}
          />
        </div>
        <Input
          id={inputId}
          type="tel"
          inputMode="tel"
          aria-label={ariaLabel}
          value={derivedState.nationalDisplayText}
          placeholder={placeholder}
          hasError={hasError || isInvalidNumberShown}
          aria-invalid={isInvalidNumberShown}
          disabled={isDisabled}
          onBlur={function markFieldLeft() {
            setHasLeftField(true);
          }}
          onChange={function updateNationalText(changeEvent) {
            handleNationalTextChange(changeEvent.target.value);
          }}
          className="flex-1 min-w-0"
        />
      </div>
      {isInvalidNumberShown && (
        <p className="text-xs font-medium text-zooerrortext mt-1">
          {t('validation:phoneForCountry', {
            country: resolveCountryDisplayName(derivedState.country, i18n.language),
          })}
        </p>
      )}
    </div>
  );
}
